// /w/:wid/rank — WORLD RANK (players or guilds, by points or kills: the podium on page one, twenty rows a page, your
// own row pinned when it is off the page, a pager) and GUILD RANK (your guild's members by points with their share
// of the guild, and the world standing around your guild).
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { RankingRow } from "@/api/types";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { ScreenHero } from "@/kit/city";
import { IconChevronDown, IconChevronLeft, IconChevronRight, IconCrown } from "@/kit/icons";
import { Bar, Button, Card, CardHead, Disc, Empty, Medallion, Row, Seg, Skeleton, cx } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { ownVillages, useInit } from "@/state/queries";
import { useBoard, useGuildMembersRanking, type BoardClass, type BoardInfo } from "@/state/socialQueries";
import { fmtNum, fmtRank, plural } from "@/game/format";
import { guildOf, guildDetail } from "@/game/guild";
import { GuildPeek } from "../guild/GuildPeek";
import { TitleBadge } from "../profile/TitleBadge";
import "../social.css";

const INFO: { value: BoardInfo; label: string; col: string }[] = [
  { value: "score", label: "Points", col: "Points" }, { value: "ksa", label: "Kills as attacker", col: "KSA" }, { value: "ksd", label: "Kills as defender", col: "KSD" }, { value: "kst", label: "Kills in all", col: "KST" },
];

export function Leaderboards() {
  return (
    <WorldPage layout="col">
      <Boards />
    </WorldPage>
  );
}

function Boards() {
  const { v, wid, base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "guild" ? "guild" : "world";
  const cls: BoardClass = params.get("show") === "guilds" ? "guild" : "user";
  const info = (INFO.some((i) => i.value === params.get("by")) ? params.get("by") : "score") as BoardInfo;
  const page = Math.max(0, Number(params.get("page") ?? 1) || 0);
  const set = (patch: Record<string, string | number | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, val] of Object.entries(patch)) { if (val === null || val === "" || (k === "page" && val === 1) || (k === "show" && val === "players") || (k === "by" && val === "score") || (k === "tab" && val === "world")) next.delete(k); else next.set(k, String(val)); }
    setParams(next, { replace: true });
  };
  const g = guildOf(v);
  const guildBoard = useBoard(wid, "guild", "score", 0, !!g);
  const myGuildRank = guildBoard.data?.output?.rank || guildDetail(g, "Rank");
  const cities = ownVillages(v, session.uid).length || (v?.village ? 1 : 0);

  return (
    <div className="stack">
      <ScreenHero
        title="Leaderboards"
        art={art.hero.leaderboard}
        artWidth="50%"
        onBack={() => nav(base)}
        stats={[
          { label: "Your rank", value: fmtRank(v?.player_stats?.rank) },
          { label: "Points", value: fmtNum(v?.player_stats?.total_points) },
          { label: "Cities", value: fmtNum(cities) },
          { label: "Guild rank", value: g ? fmtRank(myGuildRank) : "—" },
        ]}
      />
      <Seg value={tab} onChange={(t) => set({ tab: t, page: 1 })} options={[{ value: "world", label: "World rank" }, { value: "guild", label: "Guild rank" }]} />
      {tab === "world" ? (
        <WorldBoard cls={cls} info={info} page={page} onChange={set} />
      ) : (
        <GuildBoard onAllGuilds={() => set({ tab: "world", show: "guilds", page: 1 })} />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ world rank

function WorldBoard({ cls, info, page, onChange }: { cls: BoardClass; info: BoardInfo; page: number; onChange: (p: Record<string, string | number | null>) => void }) {
  const { wid, vid, v, base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const q = useBoard(wid, cls, info, page);
  const out = q.data?.output;
  const rows = out?.results ?? [];
  const total = out?.total_pages ?? 0;
  const current = out?.page_num ?? page;
  const myRank = out?.rank ?? 0;
  const g = guildOf(v);
  const [peek, setPeek] = useState<{ gid: number; tag?: string } | null>(null);
  const isMe = (r: RankingRow) => (cls === "user" ? Number(r.user_id) === session.uid : !!g && Number(r.user_id) === g.id);
  const onPage = rows.some(isMe);
  const myScore = cls === "user" ? (info === "score" ? v?.player_stats?.total_points : info === "ksa" ? v?.player_stats?.ksa : info === "ksd" ? v?.player_stats?.ksd : v?.player_stats?.kst) : undefined;
  const colLabel = INFO.find((i) => i.value === info)!.col;
  const open = (r: RankingRow) => (cls === "user" ? nav(`${base}/player/${Number(r.user_id)}`) : setPeek({ gid: Number(r.user_id) }));
  const nameOf = (r: RankingRow) => (cls === "user" ? r.user_info?.login : r.user_info?.gname) ?? `#${r.user_id}`;
  const podium = current === 1 && rows.length >= 3 ? rows.slice(0, 3) : [];
  const list = podium.length ? rows.slice(3) : rows;
  const noGuildBoard = cls === "guild" && q.data && total === 0;
  // the player's worn title badge, as the live app's rank rows show it (RankItemScript; Ben, Oct 20 2026) — players only,
  // and not when they keep titles private
  const details = useInit().data?.titles_config?.details;
  const worn = (r: RankingRow) => {
    const t = r.user_info?.titles;
    if (cls !== "user" || !t || Number(t.privacy) === 1 || !t.title_main?.title) return null;
    return { title: String(t.title_main.title), tier: Number(t.title_main.tier) || 1 };
  };
  const badge = (r: RankingRow, size: number) => {
    const w = worn(r);
    return w ? <TitleBadge title={w.title} tier={w.tier} details={details} size={size} className="lb-badge" /> : null;
  };

  return (
    <>
      <div className="lb-controls" style={{ gridTemplateColumns: "1fr 1.3fr" }}>
        <label className="gd-select">
          <span className="lbl">Show</span>
          <select value={cls === "user" ? "players" : "guilds"} onChange={(e) => onChange({ show: e.target.value, page: 1 })}>
            <option value="players">Players</option>
            <option value="guilds">Guilds</option>
          </select>
          <IconChevronDown />
        </label>
        <label className="gd-select">
          <span className="lbl">Rank by</span>
          <select value={info} onChange={(e) => onChange({ by: e.target.value, page: 1 })}>
            {INFO.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
          </select>
          <IconChevronDown />
        </label>
      </div>

      {q.isLoading && !q.data && <><Skeleton h={220} /><Skeleton h={400} /></>}
      {q.isError && !q.data && <Card><Empty>Couldn't read the board. <Button size="sm" onClick={() => void q.refetch()}>Try again</Button></Empty></Card>}
      {noGuildBoard && <Card><Empty>The game only shows the guild board to players in a guild.</Empty></Card>}

      {podium.length > 0 && (
        <Card>
          <div className="lb-podium">
            {[podium[1], podium[0], podium[2]].map((r, i) => {
              const place = i === 1 ? 1 : i === 0 ? 2 : 3;
              return (
                <button key={String(r.user_id)} type="button" className={cx("lb-step", place === 1 && "first")} onClick={() => open(r)}>
                  <span className="medwrap">
                    {place === 1 && <IconCrown className="crown" />}
                    {isMe(r) && cls === "user" ? <Medallion src={art.avatar} alt="" glow /> : cls === "guild" ? <Medallion src={art.social.crest} alt="" glow={place === 1} /> : <Medallion initial={nameOf(r)} glow={place === 1} />}
                    <span className={`place p${place}`}>{place}</span>
                  </span>
                  <span className="nm-row">{badge(r, 26)}<span className="nm">{nameOf(r)}</span></span>
                  <span className="sc"><b>{fmtNum(r.score)}</b> · {plural(r.user_info?.num_villages ?? 0, "city", "cities")}</span>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {q.data && !noGuildBoard && (
        <Card>
          <div className="lb-head"><span>Rank</span><span>Name</span><span>{colLabel}</span><span>Cities</span></div>
          <div className="rows">
            {list.map((r) => (
              <Row key={String(r.user_id)} as="button" className={cx("lb-row", isMe(r) && "you")} onClick={() => open(r)}>
                <span className="rank">{fmtNum(r.rank)}</span>
                {isMe(r) && cls === "user" ? <Medallion src={art.avatar} alt="" size={40} /> : cls === "guild" ? <Medallion src={art.social.crest} alt="" size={40} /> : <Medallion initial={nameOf(r)} size={40} />}
                {badge(r, 30)}
                <span className="nm">{nameOf(r)}{isMe(r) && <small>you</small>}</span>
                <span className="sc">{fmtNum(r.score)}</span>
                <span className="ct">{fmtNum(r.user_info?.num_villages)}</span>
                <IconChevronRight className="chev" />
              </Row>
            ))}
            {!rows.length && <Empty>Nobody is ranked here yet.</Empty>}
          </div>
        </Card>
      )}

      {q.data && !noGuildBoard && myRank > 0 && !onPage && (
        <Row as="button" className="lb-row you lb-you" onClick={() => onChange({ page: 0 })}>
          <span className="rank">{fmtRank(myRank)}</span>
          {cls === "user" ? <Medallion src={art.avatar} alt="" size={48} glow /> : <Medallion src={art.social.crest} alt="" size={48} glow />}
          <span className="nm">{cls === "user" ? session.login : g?.name}<span className="meta">{cls === "user" ? "you" : "your guild"} · tap to jump to your page</span></span>
          {myScore !== undefined && <span className="sc">{fmtNum(myScore)}</span>}
          <IconChevronRight className="chev" />
        </Row>
      )}

      {q.data && total > 1 && (
        <Card plain className="lb-pager">
          <span className="txt">Page <b>{current}</b> of {total} · 20 per page</span>
          <span className="btns">
            <Disc size="sm" label="First page" disabled={current <= 1} onClick={() => onChange({ page: 1 })}><IconChevronLeft /><IconChevronLeft style={{ marginLeft: -8 }} /></Disc>
            <Disc size="sm" label="Previous page" disabled={current <= 1} onClick={() => onChange({ page: current - 1 })}><IconChevronLeft /></Disc>
            <Disc size="sm" label="Next page" disabled={current >= total} onClick={() => onChange({ page: current + 1 })}><IconChevronRight /></Disc>
            <Disc size="sm" label="Last page" disabled={current >= total} onClick={() => onChange({ page: total })}><IconChevronRight /><IconChevronRight style={{ marginLeft: -8 }} /></Disc>
          </span>
        </Card>
      )}
      <GuildPeek wid={wid} vid={vid} target={peek} onClose={() => setPeek(null)} myGid={g?.id} level={v?.char_glevel} />
    </>
  );
}

// ------------------------------------------------------------------ guild rank

function GuildBoard({ onAllGuilds }: { onAllGuilds: () => void }) {
  const { v, wid, vid, base } = useWorldCity();
  const nav = useNavigate();
  const session = useSession();
  const g = guildOf(v);
  const members = useGuildMembersRanking(wid, !!g);
  const board = useBoard(wid, "guild", "score", 0, !!g);
  const [peek, setPeek] = useState<{ gid: number; tag?: string } | null>(null);
  const rows = useMemo(() => [...(members.data?.guild_info?.members ?? [])].sort((a, b) => a.rank - b.rank), [members.data]);
  const total = rows.reduce((a, m) => a + m.total_points, 0);
  const cities = rows.reduce((a, m) => a + m.num_villages, 0);
  const myRank = board.data?.output?.rank || guildDetail(g, "Rank") || 0;
  const around = useMemo(() => {
    const list = board.data?.output?.results ?? [];
    const i = list.findIndex((r) => g && Number(r.user_id) === g.id);
    if (i < 0) return list.slice(0, 3);
    return list.slice(Math.max(0, i - 1), Math.min(list.length, i + 2));
  }, [board.data, g]);
  const above = around.find((r) => g && r.rank === myRank - 1);

  if (!g) {
    return (
      <Card>
        <Empty>
          You're in no guild, so there is no guild rank to show.{" "}
          <Button size="sm" onClick={() => nav(`${base}/guild`)}>Find a guild</Button>
        </Empty>
      </Card>
    );
  }
  return (
    <>
      <Card className="hero c-hero gd-hero" bare>
        <div className="body">
          <div className="gd-herorow">
            <Medallion src={art.social.crest} alt="" size={96} glow />
            <div className="gd-herotxt">
              <h2>{g.name}</h2>
              <div className="sub"><b>[{g.tag}]</b> · World rank {fmtRank(myRank)} · {plural(rows.length || g.members.length, "member")}</div>
            </div>
          </div>
        </div>
        <div className="statband">
          <div><div className="lbl">Total points</div><div className="val">{fmtNum(total)}</div></div>
          <div><div className="lbl">Average</div><div className="val">{fmtNum(rows.length ? Math.round(total / rows.length) : 0)}</div></div>
          <div><div className="lbl">Cities</div><div className="val">{fmtNum(cities)}</div></div>
        </div>
      </Card>

      <Card>
        <CardHead title="Members" aside="ranked by points" />
        <div className="lb-head"><span>Rank</span><span>Name · share</span><span>Points</span><span>Cities</span></div>
        {members.isLoading && !members.data && <Skeleton h={200} />}
        <div className="rows">
          {rows.map((m) => {
            const me = m.user_id === session.uid;
            const share = total ? m.total_points / total : 0;
            return (
              <Row key={m.user_id} as="button" className={cx("lb-row", me && "you")} onClick={() => nav(`${base}/player/${m.user_id}`)} style={{ minHeight: "calc(var(--u) * 84)" }}>
                <span className={cx("rank")}><span className={cx("place", m.rank <= 3 && `p${m.rank}`)} style={m.rank <= 3 ? { display: "inline-grid", placeItems: "center", width: "calc(var(--u) * 34)", height: "calc(var(--u) * 34)", borderRadius: "50%", color: "#1B1208", fontWeight: 700, background: m.rank === 1 ? "linear-gradient(180deg,#F6E7B8,#B8892F)" : m.rank === 2 ? "linear-gradient(180deg,#E6E8EC,#8A9099)" : "linear-gradient(180deg,#E0A070,#8C4A26)" } : undefined}>{m.rank}</span></span>
                {me ? <Medallion src={art.avatar} alt="" size={52} glow /> : <Medallion initial={m.login} size={52} />}
                <span className="nm" style={{ display: "flex", flexDirection: "column", gap: "calc(var(--u) * 6)" }}>
                  <span>{m.login}{me && <small>you</small>}</span>
                  <span className="lb-share"><Bar value={share} /><small>{Math.round(share * 100)}%</small></span>
                </span>
                <span className="sc">{fmtNum(m.total_points)}</span>
                <span className="ct">{fmtNum(m.num_villages)}</span>
                <IconChevronRight className="chev" />
              </Row>
            );
          })}
          {members.data && !rows.length && <Empty>The guild's points haven't been tallied yet.</Empty>}
        </div>
      </Card>

      <Card className="lb-standing">
        <CardHead title="World standing" aside="guilds around you" />
        {board.isLoading && !board.data && <Skeleton h={120} />}
        <div className="rows">
          {around.map((r) => {
            const me = Number(r.user_id) === g.id;
            return (
              <Row key={String(r.user_id)} as="button" className={cx("lb-row", me && "you")} onClick={() => (me ? undefined : setPeek({ gid: Number(r.user_id) }))}>
                <span className="rank">{fmtNum(r.rank)}</span>
                <Medallion src={art.social.crest} alt="" size={40} />
                <span className="nm">{r.user_info?.gname ?? `Guild ${r.user_id}`}</span>
                <span className="sc">{fmtNum(r.score)}</span>
                <span className="ct">{fmtNum(r.user_info?.num_villages)}</span>
                <IconChevronRight className="chev" style={{ visibility: me ? "hidden" : undefined }} />
              </Row>
            );
          })}
          {board.data && !around.length && <Empty>Your guild isn't on the board yet.</Empty>}
        </div>
        <div className="lb-foot">
          <span>{above ? `${fmtNum(above.score - (around.find((r) => Number(r.user_id) === g.id)?.score ?? 0))} points behind #${above.rank} ${above.user_info?.gname ?? ""}` : myRank === 1 ? "Your guild leads the world." : ""}</span>
          <button type="button" className="link caps" onClick={onAllGuilds}>All guilds <IconChevronRight /></button>
        </div>
      </Card>
      <GuildPeek wid={wid} vid={vid} target={peek} onClose={() => setPeek(null)} myGid={g.id} level={v?.char_glevel} />
    </>
  );
}
